import {
  getInstance,
  registerRemotes,
} from '@module-federation/enhanced/runtime';
import type { SharedFunction } from '@nx/module-federation';
import {
  EAGER,
  HOST_SHARED_LIB_VERSION,
  HOST_SINGLETON,
  PALDESK_HOST_CONFIG,
  PALDESK_HOST_FEDERATION_RUNTIME,
  PALDESK_REMOTE_CONFIGS,
  REMOTE_SHARED_LIB_VERSION,
  REMOTE_SINGLETON,
  VERSIONED_SHARED_LIBS,
} from './module-federation.consts';

import type {
  HostEnvironment,
  PaldeskFederationRuntime,
  PaldeskHostConfig,
  PaldeskRemoteManifest,
  PaldeskRemoteManifestMap,
  PaldeskRemoteVersions,
  RemoteUrlParams,
  RemoteVersionConfig,
  RemoteVersionEnvConfig,
  SharedConfigOverride,
} from './module-federation.models';
import { REMOTE_LOCALHOSTS } from './remotes-localhost.registry';

/**
 * Compile-time flag, not a runtime binding: production builds replace it with
 * `false` (`@angular/build` sets `define: { ngDevMode: 'false' }` whenever
 * script optimization is on), development builds leave it undefined and expect
 * `@angular/core` to create the global. Only referenced behind `typeof`.
 */
declare const ngDevMode: unknown;

/**
 * Remote descriptor this lib hands to `registerRemotes`. Name and entry are all
 * it sets; the rest of the vendor `Remote` type is optional, and assignability
 * is still enforced at the `registerRemotes` call in `prepareHost`.
 * Not imported from `@module-federation/runtime-core`: transitive dep, no root
 * `package.json` version, so Nx warns "will not be shared" on every host build.
 * Declaring it would also make it a shared singleton — `SINGLETON_PACKAGES`
 * matches the bare `@module-federation` prefix — for a type-only import.
 */
interface RemoteManifestEntry {
  name: string;
  entry: string;
}

export class PaldeskModuleFederation {
  private static _isRemoteVersionEnvConfig(
    config: RemoteVersionConfig,
  ): config is RemoteVersionEnvConfig {
    return !['local', 'e2e'].includes(config.environment);
  }
  static get hostRemotes(): PaldeskRemoteManifestMap {
    const remotes = Reflect.get(window, PALDESK_REMOTE_CONFIGS) || {};
    return remotes;
  }

  static get hostConfig(): PaldeskHostConfig {
    const hostConfig = Reflect.get(window, PALDESK_HOST_CONFIG);
    if (!hostConfig) {
      throw new Error(
        this.formatModuleFederationMessage(
          'Host config is not initialized. Ensure prepareHost() has been called and awaited before accessing hostConfig.',
        ),
      );
    }
    return hostConfig;
  }

  static get hostMfRuntime(): PaldeskFederationRuntime | undefined {
    return Reflect.get(window, PALDESK_HOST_FEDERATION_RUNTIME);
  }

  static getRemoteConfig<C = unknown>(
    remoteName: string,
  ): PaldeskRemoteManifest<C> | undefined {
    const remoteConfigs = Reflect.get(window, PALDESK_REMOTE_CONFIGS) || {};

    if (!remoteConfigs[remoteName]) {
      console.warn(
        this.formatModuleFederationMessage(
          `No config found for remote "${remoteName}". Make sure the host has the correct remote configuration and that the remote is listed in the host's remotes configuration.`,
        ),
      );
    }
    return remoteConfigs[remoteName];
  }

  private static _getMajorVersion(version: string): string {
    const majorVersionMatch = version.match(/^(\d+)\./);
    return majorVersionMatch ? majorVersionMatch[1] : version;
  }

  private static _usingLatestOfVersion(version: string): boolean {
    return version.endsWith('.*');
  }

  private static _getVersion(version: string) {
    if (this._usingLatestOfVersion(version)) {
      return 'latest';
    }
    return version;
  }

  private static _getRemoteBaseUrl(params: RemoteUrlParams): string {
    const { hostEnv, versionConfig, remoteName } = params;

    if (versionConfig.environment === 'local') {
      if (hostEnv !== 'mfe-local') {
        console.warn(
          this.formatModuleFederationMessage(
            `Remote "${remoteName}" is set to "local" version, but environment is "${hostEnv}". This may lead to unexpected issues. Consider setting environment to "mfe-local" for local development.`,
          ),
        );
      }

      const localhost = REMOTE_LOCALHOSTS[remoteName];

      if (!localhost) {
        throw new Error(
          this.formatModuleFederationMessage(
            `Remote "${remoteName}" has no localhost entry registered. Add it to REMOTE_LOCALHOSTS.`,
          ),
        );
      }

      return `http://${localhost}`;
    }

    // Direct URL (CI e2e): the remote was uploaded to storage by the pipeline
    // pre-step and the `local` declaration was overridden to this URL.
    if (versionConfig.environment === 'e2e') {
      return versionConfig.url;
    }

    if (!this._isRemoteVersionEnvConfig(versionConfig)) {
      throw new Error(
        this.formatModuleFederationMessage(
          `Remote "${remoteName}" has invalid configuration. Version is required.`,
        ),
      );
    }

    return `https://static2-${versionConfig.environment}.paldesk.palfinger.com/mfe/${remoteName}/v${this._getMajorVersion(versionConfig.version)}/${this._getVersion(versionConfig.version)}`;
  }

  private static _getRemoteEntryUrl(params: RemoteUrlParams): string {
    return `${PaldeskModuleFederation.getRemotePublicUrl(params)}/mf-manifest.json?v=${Date.now()}`;
  }

  private static _buildRemoteManifestEntries(params: {
    versions: PaldeskRemoteVersions;
    environment: HostEnvironment;
  }): RemoteManifestEntry[] {
    const manifestEntry: RemoteManifestEntry[] = [];

    Object.entries(params.versions).forEach(([remoteName, version]) => {
      manifestEntry.push({
        entry: this._getRemoteEntryUrl({
          remoteName,
          hostEnv: params.environment,
          versionConfig: version,
        }),
        name: remoteName,
      });
    });

    return manifestEntry;
  }

  private static async _fetchRemoteConfigs(
    remotes: PaldeskRemoteVersions,
  ): Promise<Record<string, PaldeskRemoteManifest>> {
    const configs: Record<string, PaldeskRemoteManifest> = {};

    await Promise.all(
      Object.entries(remotes).map(async ([remoteName, version]) => {
        const remoteConfigUrl = this.getRemoteConfigUrl({
          remoteName,
          versionConfig: version,
        });

        const response = await fetch(remoteConfigUrl);
        if (!response.ok) {
          throw new Error(
            this.formatModuleFederationMessage(
              `Failed to fetch config for "${remoteName}" from ${remoteConfigUrl}: ${response.status} ${response.statusText}`,
            ),
          );
        }

        const config = await response.json();
        configs[remoteName] = { config, version, buildId: config.buildId };
      }),
    );

    return configs;
  }

  static formatModuleFederationMessage(message: string) {
    return `[Paldesk Module Federation] ${message}`;
  }

  static getRemotePublicUrl(params: RemoteUrlParams): string {
    return this._getRemoteBaseUrl(params);
  }

  static getRemoteConfigUrl(
    params: RemoteUrlParams & { buildId?: string },
  ): string {
    // `remote-environment-config.json` ships as a token template whose values
    // are merged in at deploy time (deployments/templates/deploy_mfe.yml). The
    // e2e prepare stage only builds and uploads, so that copy still reads
    // "will be replaced" and every API call goes to /will%20be%20replaced/...
    // `.local.json` is committed with real dev/test values and is copied into
    // the build output, so e2e reads it just like a locally served remote does
    // — which is exactly what these remotes did before they moved to storage.
    const usesLocalConfig = ['local', 'e2e'].includes(
      params.versionConfig.environment,
    );
    return `${PaldeskModuleFederation.getRemotePublicUrl(params)}/assets/config/remote-environment-config${usesLocalConfig ? '.local' : ''}.json?v=${params.buildId ?? Date.now()}`;
  }

  /**
   * Create the `ngDevMode` global before any Angular module evaluates.
   *
   * Angular reads a bare `ngDevMode` at module scope — e.g. `FALSE_SIGNAL` in
   * `@angular/forms` `_validation_errors-chunk.mjs` (22.0.7) — and relies on
   * `@angular/core` having created the global first. Under module federation
   * that ordering is not guaranteed: `@angular/core` and each of its sub-entry
   * points (`/primitives/signals`, `/primitives/di`, `/rxjs-interop`) are
   * separate shared singletons, so a package can resolve `computed`/`SIGNAL`
   * while `@angular/core` itself has not executed and the global is still
   * missing — a `ReferenceError` while the chunk is being evaluated.
   *
   * This has to run before the first Angular module body. `main.ts` imports no
   * Angular and reaches `./bootstrap` through a dynamic import, so `prepareHost`
   * is the last hook that qualifies — which is why the older copies of this
   * workaround inside `bootstrap.ts` files never fired: ES imports are hoisted,
   * so the whole root-component graph had already evaluated by the time they
   * ran.
   *
   * The value follows the build. Production replaces the identifier with
   * `false`; development leaves it undefined and gets `{}`, which is truthy, so
   * `@angular/core` still runs `initNgDevMode()` and fills in its counters. The
   * `in` check leaves an already-created global untouched.
   */
  private static _defineNgDevMode(): void {
    if ('ngDevMode' in globalThis) {
      return;
    }

    Reflect.set(
      globalThis,
      'ngDevMode',
      typeof ngDevMode === 'undefined' ? {} : ngDevMode,
    );
  }

  static async prepareHost(options: {
    hostName: string;
    env: HostEnvironment;
    remotes?: PaldeskRemoteVersions;
  }) {
    const { hostName, env, remotes = {} } = options;

    this._defineNgDevMode();

    try {
      const config = {
        name: hostName,
        remotes: this._buildRemoteManifestEntries({
          versions: remotes,
          environment: env,
        }),
      };

      Reflect.set(window, PALDESK_HOST_CONFIG, { environment: env });

      const mfInstance = getInstance();

      Reflect.set(window, PALDESK_HOST_FEDERATION_RUNTIME, mfInstance);

      registerRemotes(config.remotes);

      const remoteConfigs = await this._fetchRemoteConfigs(remotes);

      // Store the fetched remote configs on the window object for access by remotes
      Reflect.set(window, PALDESK_REMOTE_CONFIGS, remoteConfigs);

      return mfInstance;
    } catch (error) {
      console.error(
        this.formatModuleFederationMessage(
          'Error preparing host for module federation:',
        ),
        error,
      );
      throw error;
    }
  }
  private static _createSharedConfig(
    singletons: string[],
    providedVersion: string,
    override?: SharedConfigOverride,
  ): SharedFunction {
    return (name: string) => {
      const isEager = EAGER.some((pkg) => name?.startsWith(pkg));
      const isSingleton = singletons.some((pkg) => name?.startsWith(pkg));
      // A synthetic version exists only to give an *unversioned* lib enough
      // identity to be negotiable: without one webpack registers it as '0',
      // host and remote collide on that single share-scope key, and whichever
      // registers first wins. npm packages already carry real versions and are
      // deliberately left alone.
      //
      // `!isEager` is load-bearing, not defensive. An eager module is executed
      // from the initial chunk before the share scope can be negotiated, so a
      // version that differs between host and remote yields two *live* copies
      // rather than one agreed copy. Doing that to
      // `@paldesk/module-federation/core` splits the federation runtime into
      // two share scopes, which duplicates `@angular/core` — Angular CDK then
      // builds `A11yModule` against a different copy than the one holding the
      // active injection context and throws NG0203.
      //
      // Only the host declares a version. Remotes stay unversioned so their
      // emitted config is byte-identical to what it was before versioning
      // existed — an app that shells *itself* in e2e (job-planner) builds both
      // sides from this same remote config, so forcing a version there changes
      // the share-scope key on both sides at once and buys nothing.
      const isVersionedLib =
        !isEager &&
        providedVersion !== REMOTE_SHARED_LIB_VERSION &&
        VERSIONED_SHARED_LIBS.some((p) => name?.startsWith(p));

      if (isEager || isSingleton) {
        return {
          singleton: isSingleton,
          strictVersion: false,
          requiredVersion: false,
          ...(isVersionedLib ? { version: providedVersion } : {}),
          eager: isEager,
        };
      }

      const overrideResult = override?.(name);

      if (overrideResult !== undefined) return overrideResult;

      return false;
    };
  }

  static async replaceEnvironmentFile(path: string, target: object) {
    try {
      const response = await fetch(`${path}?timestamp=${Date.now()}`);
      if (!response.ok) {
        throw new Error(
          this.formatModuleFederationMessage(
            `Failed to fetch environment file from ${path}: ${response.status} ${response.statusText}`,
          ),
        );
      }
      const data = await response.json();
      Object.assign(target, data);
    } catch (error) {
      console.error(
        this.formatModuleFederationMessage('Error replacing environment file:'),
        error,
      );
      throw error;
    }
  }

  static createRemoteSharedConfig(
    override?: SharedConfigOverride,
  ): SharedFunction {
    return this._createSharedConfig(
      REMOTE_SINGLETON,
      REMOTE_SHARED_LIB_VERSION,
      override,
    );
  }

  static createHostSharedConfig(
    override?: SharedConfigOverride,
  ): SharedFunction {
    return this._createSharedConfig(
      HOST_SINGLETON,
      HOST_SHARED_LIB_VERSION,
      override,
    );
  }
}
